<script setup lang="ts" generic="T extends string | number">
/**
 * The plain radio of an NxRadioGroup: a round dot and its text beside it —
 * „Mit Zellen verschieben und Größe ändern“ in a list of options. The default
 * slot is the visible text (it names the option, so `label` is only needed
 * without one). Card and swatch looks are NxRadioCardItem / NxRadioSwatchItem.
 */
import NxRadioItem from './NxRadioItem.vue';
import NxSaveStateBadge from '../NxSaveStateBadge.vue';
import type { NxRadioItemBaseProps } from './radio-internal';

const props = defineProps<NxRadioItemBaseProps<T>>();
</script>

<template>
  <NxRadioItem v-bind="props">
    <template #default="{ checked, disabled, saveState, errorMessage, saveFadeOut }">
      <span class="nx-radio-dot-item" :class="{ checked, disabled }" :data-save-status="saveState">
        <span class="nx-radio-dot-item__dot" aria-hidden="true" />
        <span class="nx-radio-dot-item__text"><slot /></span>
      </span>
      <!-- Sibling of the visual (anchored to .nx-radio) so the disabled fade
           never dims the badge. -->
      <NxSaveStateBadge
        v-if="checked"
        class="save-badge"
        :state="saveState"
        :error-message="errorMessage"
        :fade-out="saveFadeOut"
      />
    </template>
  </NxRadioItem>
</template>

<style scoped>
.nx-radio-dot-item {
  display: inline-flex;
  align-items: center;
  gap: var(--nxt-space-xs);
  color: var(--nxt-fg);
  font-size: var(--nxt-font-size-sm);
  line-height: var(--nxt-line-height-control);
  cursor: pointer;
}
.nx-radio-dot-item__dot {
  display: inline-grid;
  flex: 0 0 auto;
  place-items: center;
  width: var(--nxt-icon-size-sm);
  height: var(--nxt-icon-size-sm);
  border: var(--nxt-hairline) solid var(--nxt-radio-border);
  border-radius: var(--nxt-radius-full);
  background: var(--nxt-radio-bg);
  transition: border-color var(--nxt-motion-fast);
}
.nx-radio-dot-item:hover:not(.disabled) .nx-radio-dot-item__dot {
  border-color: var(--nxt-radio-border-hover);
}
.nx-radio-dot-item.checked .nx-radio-dot-item__dot {
  border-color: var(--nxt-radio-bg-checked);
  background: var(--nxt-radio-bg-checked);
}
.nx-radio-dot-item.checked .nx-radio-dot-item__dot::after {
  content: '';
  width: 40%;
  height: 40%;
  border-radius: var(--nxt-radius-full);
  background: var(--nxt-radio-dot);
}
.nx-radio-dot-item.disabled {
  color: var(--nxt-disabled-fg);
}
.nx-radio-dot-item.disabled .nx-radio-dot-item__dot {
  border-color: var(--nxt-border);
  background: var(--nxt-radio-bg-disabled);
}
/* Save status: badge on the CHECKED item only, trailing its text and centred
   on the line (the badge leaves its positioning to the host). Absolute, so it
   never shifts the layout or covers the label. */
.save-badge {
  position: absolute;
  top: 50%;
  inset-inline-start: calc(100% + var(--nxt-space-2xs));
  translate: 0 -50%;
}
@media (prefers-reduced-motion: reduce) {
  .nx-radio-dot-item__dot {
    transition: none;
  }
}
</style>
